import type { Company, CompanyCreate, Lab } from "@/lib/api_client";

export type CompanyFlowType = Company["flowType"];

export const flowTypeLabel = (flowType: CompanyFlowType): string => {
  switch (flowType) {
    case "healthCheck":
      return "Helsekontroll";
    case "consumer":
      return "Vanlig b2c produkt";
    case "drDropinBht":
      return "Dr.Dropin BHT";
    case "kickstart":
      return "Kickstart";
  }
};

/**
 * Flows that can point at an internal (non-public) lab. Consumer companies
 * have no lab of their own — the user picks from the public list.
 */
export const usesInternalLab = (flowType: CompanyFlowType): boolean =>
  flowType === "healthCheck" ||
  flowType === "drDropinBht" ||
  flowType === "kickstart";

/**
 * Flows that cannot function without one. healthCheck and drDropinBht pin the
 * lab onto every visit at creation (backend/src/modules/accounts/routes.ts), so
 * a company without one produces visits with no draw site.
 *
 * Kickstart is deliberately not here: its visits are created with labId null
 * and the employee picks a lab through the normal B2C flow. The company lab is
 * only an *offer* — the labs policy surfaces that partner-only draw site to the
 * company's employees when it's set (backend/src/modules/labs/routes.ts), and
 * when it isn't they simply pick from the public list.
 */
export const requiresInternalLab = (flowType: CompanyFlowType): boolean =>
  flowType === "healthCheck" || flowType === "drDropinBht";

export interface CompanyFormState {
  name: string;
  bookingUrl: string;
  logoUrl: string;
  labId: string;
  deactivatedAt: string;
  flowType: CompanyFlowType;
  visitsPerYear: number;
  allowBloodTestRequest: boolean;
  incomePerEmployee: string;
}

export const emptyForm: CompanyFormState = {
  name: "",
  bookingUrl: "",
  logoUrl: "",
  labId: "",
  deactivatedAt: "",
  flowType: "healthCheck",
  visitsPerYear: 1,
  allowBloodTestRequest: true,
  incomePerEmployee: "",
};

export function companyToForm(company: Company): CompanyFormState {
  return {
    name: company.name,
    bookingUrl: company.bookingUrl ?? "",
    logoUrl: company.logoUrl ?? "",
    labId: company.labId ?? "",
    deactivatedAt: company.deactivatedAt
      ? company.deactivatedAt.slice(0, 16)
      : "",
    flowType: company.flowType ?? "healthCheck",
    visitsPerYear: company.visitsPerYear ?? 1,
    allowBloodTestRequest: company.allowBloodTestRequest ?? true,
    incomePerEmployee: company.incomePerEmployee ?? "",
  };
}

export function formToPayload(form: CompanyFormState): CompanyCreate {
  return {
    name: form.name,
    bookingUrl: form.bookingUrl || null,
    logoUrl: form.logoUrl || null,
    labId: form.flowType === "consumer" ? null : form.labId || null,
    deactivatedAt: form.deactivatedAt
      ? new Date(form.deactivatedAt).toISOString()
      : null,
    flowType: form.flowType,
    visitsPerYear: form.visitsPerYear,
    allowBloodTestRequest: form.allowBloodTestRequest,
    incomePerEmployee: normalizeIncome(form.incomePerEmployee) || null,
  };
}

function normalizeIncome(raw: string): string {
  return raw.trim().replace(",", ".");
}

export type FieldErrors = Partial<Record<keyof CompanyFormState, string>>;

/** Rule 10: every error belongs to a field, so the panel can show it there. */
export function validateForm(
  form: CompanyFormState,
  labs: Pick<Lab, "id" | "public">[],
): FieldErrors {
  const errors: FieldErrors = {};
  if (!form.name.trim()) errors.name = "Navn er påkrevd";

  if (usesInternalLab(form.flowType)) {
    const lab = labs.find((l) => l.id === form.labId);
    if (!lab && requiresInternalLab(form.flowType)) {
      errors.labId = "Lab er påkrevd for denne flyten";
    } else if (lab?.public) {
      // Public labs are never a partner draw site, whether required or not.
      errors.labId = "Lab må være intern (ikke offentlig)";
    }
  }

  const income = normalizeIncome(form.incomePerEmployee);
  if (income && !(Number(income) >= 0)) {
    errors.incomePerEmployee = "Må være et positivt tall";
  }
  return errors;
}

export function isDirty(a: CompanyFormState, b: CompanyFormState): boolean {
  return (Object.keys(emptyForm) as (keyof CompanyFormState)[]).some(
    (key) => a[key] !== b[key],
  );
}

export type RowSaveState = "saving" | "saved" | "error";
export type RowStatusTone = RowSaveState | "dirty";

/**
 * Rule 6: the row is the status line. A failed save outranks the dirty mark —
 * both are true at once, and "Feilet" is the one that needs a click.
 */
export function rowStatus(
  dirty: boolean,
  save: RowSaveState | undefined,
): { label: string; tone: RowStatusTone } | null {
  if (save === "saving") return { label: "Lagrer…", tone: "saving" };
  if (save === "error") return { label: "Feilet", tone: "error" };
  if (dirty) return { label: "Ulagret", tone: "dirty" };
  if (save === "saved") return { label: "Lagret", tone: "saved" };
  return null;
}

export function isDeactivated(company: Company, now = new Date()): boolean {
  if (!company.deactivatedAt) return false;
  return new Date(company.deactivatedAt) <= now;
}

export function matchesSearch(
  company: Company,
  query: string,
  labName?: string | null,
): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [
    company.name,
    company.slug,
    flowTypeLabel(company.flowType),
    labName ?? "",
  ].some((field) => field.toLowerCase().includes(q));
}

export function loginUrl(slug: string): string {
  return `https://app.amino.no/login/${slug}`;
}

export function qrFileName(name: string, slug: string): string {
  const safeName = name.replace(/[\\/:*?"<>|]/g, "-").trim();
  return `${safeName}-${slug}`;
}

export function initials(name: string): string {
  const trimmed = name.trim();
  return trimmed ? trimmed[0]!.toUpperCase() : "?";
}
